/**
 * Platform page explaining the security advantages of the M365 platform.
 * Public-facing page for unauthenticated users.
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MarketingLayout } from './MarketingLayout';
import './PlatformPage.css';

interface SecurityStat {
    value: string;
    label: string;
    detail: string;
}

const securityStats: SecurityStat[] = [
    { value: '$20B', label: 'Investment', detail: 'Microsoft security investment over 5 years' },
    { value: '34,000', label: 'Engineers', detail: 'Full-time security engineers worldwide' },
    { value: '100T', label: 'Signals', detail: 'Security signals processed daily' },
];

interface Certification {
    name: string;
    description: string;
}

const certifications: Certification[] = [
    { name: 'SOC 2 Type 2', description: 'Independent audit of security controls' },
    { name: 'ISO 27001', description: 'Information security management' },
    { name: 'ISO 27017', description: 'Cloud security controls' },
    { name: 'ISO 27018', description: 'Cloud privacy protection' },
    { name: 'FedRAMP', description: 'US federal security authorization' },
    { name: 'HIPAA', description: 'Healthcare data protection' },
];

interface SecurityFeature {
    icon: string;
    title: string;
    description: string;
    capabilities: string[];
}

const securityFeatures: SecurityFeature[] = [
    {
        icon: '🛡️',
        title: 'Zero Trust Architecture',
        description: 'Never trust, always verify. Every access request is authenticated and authorized using Microsoft Entra ID with Conditional Access policies.',
        capabilities: [
            'Continuous access evaluation',
            'Risk-based sign-in detection',
            'Real-time session monitoring',
            'Device compliance verification',
        ],
    },
    {
        icon: '🔐',
        title: 'Conditional Access',
        description: 'Microsoft\'s Zero Trust policy engine evaluates signals from users, devices, locations, and workloads to enforce granular access controls.',
        capabilities: [
            'Multi-factor authentication enforcement',
            'Location-based access policies',
            'Device state requirements',
            'Session timeout controls',
        ],
    },
    {
        icon: '🔍',
        title: 'Threat Intelligence',
        description: 'AI-driven threat detection powered by 100 trillion daily security signals. Microsoft blocks 4.5 million malware attempts and screens 5 billion emails every day.',
        capabilities: [
            'Real-time threat detection',
            'Automated incident response',
            'Phishing protection',
            'Malware prevention',
        ],
    },
    {
        icon: '📊',
        title: 'Audit and Compliance',
        description: 'Complete activity logging with immutable audit trails. Meet regulatory requirements with built-in compliance tools and reporting.',
        capabilities: [
            'Immutable audit logs',
            'Compliance reporting',
            'eDiscovery capabilities',
            'Data retention policies',
        ],
    },
];

interface ComparisonPoint {
    category: string;
    microsoft: string;
    proprietary: string;
}

const comparisonPoints: ComparisonPoint[] = [
    {
        category: 'Security Investment',
        microsoft: '$20 billion over 5 years, 34,000 dedicated security engineers',
        proprietary: 'Limited budget and team size typical of startups',
    },
    {
        category: 'Threat Intelligence',
        microsoft: '100 trillion signals daily from global Microsoft ecosystem',
        proprietary: 'Limited visibility confined to own customer base',
    },
    {
        category: 'Compliance Certifications',
        microsoft: 'SOC 2, ISO 27001, FedRAMP, HIPAA, HITRUST, and 90+ others',
        proprietary: 'Often limited to SOC 2, if any',
    },
    {
        category: 'Security Team Coverage',
        microsoft: '24/7 global security operations with dedicated response teams',
        proprietary: 'Small teams with limited coverage hours',
    },
    {
        category: 'Track Record',
        microsoft: 'Decades of enterprise security, protecting Fortune 500 companies',
        proprietary: 'Limited operational history and proven resilience',
    },
    {
        category: 'Identity Protection',
        microsoft: 'Microsoft Entra ID with risk-based Conditional Access',
        proprietary: 'Basic authentication, often without adaptive controls',
    },
];

export function PlatformPage(): JSX.Element {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            navigate('/app/workspaces', { replace: true });
        }
    }, [isAuthenticated, isLoading, navigate]);

    return (
        <MarketingLayout>
            {/* Hero Section */}
            <section className="platform-hero-section">
                <div className="platform-hero-container">
                    <span className="platform-hero-badge">Enterprise-Grade Security</span>
                    <h1 className="platform-hero-headline">
                        Built on Microsoft 365
                    </h1>
                    <p className="platform-hero-subheadline">
                        Why trust a startup with your most sensitive incident response communications
                        when you can leverage the security infrastructure that protects
                        Fortune 500 companies worldwide?
                    </p>
                </div>
            </section>

            {/* Security Stats */}
            <section className="platform-stats-section">
                <div className="platform-stats-container">
                    <div className="platform-stats-grid">
                        {securityStats.map((stat) => (
                            <div key={stat.label} className="platform-stat-card">
                                <span className="platform-stat-value">{stat.value}</span>
                                <span className="platform-stat-label">{stat.label}</span>
                                <span className="platform-stat-detail">{stat.detail}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* The Argument Section */}
            <section className="platform-argument-section">
                <div className="platform-argument-container">
                    <div className="platform-argument-content">
                        <span className="platform-argument-eyebrow">The Security Question</span>
                        <h2 className="platform-argument-headline">
                            Proprietary platforms ask you to trust their security. We ask you to trust Microsoft&apos;s.
                        </h2>
                        <p className="platform-argument-text">
                            When evaluating incident response platforms, consider: who is protecting the platform itself?
                            Proprietary solutions require you to trust that a small security team can defend against
                            nation-state actors and sophisticated threat groups. With IRPilot, your data is protected
                            by the same infrastructure that secures Microsoft&apos;s own operations and those of
                            governments, financial institutions, and enterprises worldwide.
                        </p>
                    </div>
                    <div className="platform-argument-highlight">
                        <div className="platform-highlight-card">
                            <span className="platform-highlight-number">5B</span>
                            <span className="platform-highlight-text">
                                emails screened daily for malware and phishing
                            </span>
                        </div>
                        <div className="platform-highlight-card">
                            <span className="platform-highlight-number">4.5M</span>
                            <span className="platform-highlight-text">
                                malware attempts blocked every single day
                            </span>
                        </div>
                        <div className="platform-highlight-card">
                            <span className="platform-highlight-number">$4B</span>
                            <span className="platform-highlight-text">
                                in fraud attempts thwarted last year
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Security Features */}
            <section className="platform-features-section">
                <div className="platform-features-container">
                    <h2 className="section-title">Enterprise Security, Built In</h2>
                    <p className="section-subtitle">
                        Every IRPilot workspace inherits Microsoft 365&apos;s security architecture
                    </p>
                    <div className="platform-features-grid">
                        {securityFeatures.map((feature) => (
                            <div key={feature.title} className="platform-feature-card">
                                <div className="platform-feature-icon">{feature.icon}</div>
                                <h3 className="platform-feature-title">{feature.title}</h3>
                                <p className="platform-feature-description">{feature.description}</p>
                                <ul className="platform-feature-capabilities">
                                    {feature.capabilities.map((capability) => (
                                        <li key={capability}>{capability}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Certifications */}
            <section className="platform-certifications-section">
                <div className="platform-certifications-container">
                    <h2 className="section-title">Compliance You Can Verify</h2>
                    <p className="section-subtitle">
                        Microsoft 365 maintains 90+ compliance certifications, independently audited
                    </p>
                    <div className="platform-certifications-grid">
                        {certifications.map((cert) => (
                            <div key={cert.name} className="platform-certification-card">
                                <span className="platform-certification-name">{cert.name}</span>
                                <span className="platform-certification-description">{cert.description}</span>
                            </div>
                        ))}
                    </div>
                    <p className="platform-certifications-note">
                        Access audit reports directly from the IRPilot Portal
                    </p>
                </div>
            </section>

            {/* Comparison Section */}
            <section className="platform-comparison-section">
                <div className="platform-comparison-container">
                    <h2 className="section-title">Microsoft 365 vs. Proprietary Platforms</h2>
                    <p className="section-subtitle">
                        A realistic comparison of security capabilities
                    </p>
                    <div className="platform-comparison-table-wrapper">
                        <table className="platform-comparison-table">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th className="highlight-column">Microsoft 365 (IRPilot)</th>
                                    <th>Proprietary Platforms</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comparisonPoints.map((point) => (
                                    <tr key={point.category}>
                                        <td className="category-cell">{point.category}</td>
                                        <td className="microsoft-cell">{point.microsoft}</td>
                                        <td className="proprietary-cell">{point.proprietary}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="platform-cta-section">
                <div className="platform-cta-container">
                    <h2 className="platform-cta-title">Enterprise security without the complexity</h2>
                    <p className="platform-cta-description">
                        IRPilot gives you Microsoft&apos;s security infrastructure with pre-configured
                        policies optimized for incident response. No security team required.
                    </p>
                    <div className="platform-cta-buttons">
                        <Link to="/login" className="platform-cta-primary">
                            Get Started
                        </Link>
                        <Link to="/pricing" className="platform-cta-secondary">
                            View Pricing
                        </Link>
                    </div>
                </div>
            </section>
        </MarketingLayout>
    );
}
